"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Wrench, Briefcase, PhoneIncoming, ClipboardCheck, Zap, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HomeProfessionalServices() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll();
  return <section id="home-professional-services" className="w-full relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[800px]">
        
        {}
        <div ref={leftRef} className={`
            relative flex flex-col justify-center p-8 md:p-16 lg:p-24 bg-muted/50 border-b lg:border-b-0 lg:border-r border-border
            transition-all duration-1000 ease-out
            ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
          `}>
          {}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 max-w-xl mx-auto lg:mx-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-white rounded-lg shadow-sm border border-border/50">
                <Wrench className="w-6 h-6 text-primary" />
              </div>
              <span className="text-sm font-semibold tracking-wider text-muted-foreground uppercase">
                Home Services
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              Emergency Dispatch <br />
              <span className="text-primary">Made Instant.</span>
            </h2>

            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              For HVAC, Plumbing, and Electrical businesses, missing a call means missing a job. 
              Our AI agents handle 24/7 emergency dispatch, qualifying urgency and routing technicians immediately.
            </p>

            <ul className="space-y-4 mb-10">
              {[{
              icon: Zap,
              text: "Instant After-Hours Response"
            }, {
              icon: PhoneIncoming,
              text: "Smart Urgency Qualification"
            }, {
              icon: Clock,
              text: "Automated Technician Scheduling"
            }].map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-foreground font-medium">{item.text}</span>
                </li>)}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to="/SolutionsPage">
                  View Solutions
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 bg-transparent border-primary/20 hover:bg-primary/5">
                <Link to="/ContactUsPage">
                  Request Demo
                </Link>
              </Button>
            </div>

            {}
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-background group">
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent z-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/home_services_dispatch_interface.png" alt="AI Emergency Dispatch Interface" className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute bottom-4 left-4 right-4 z-20">
                <div className="bg-card/95 backdrop-blur-sm p-4 rounded-lg border border-border shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <p className="text-xs font-medium text-muted-foreground">
                      AI Agent: "I've dispatched a technician to your location. ETA is 20 minutes."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`
            relative flex flex-col justify-center p-8 md:p-16 lg:p-24 bg-background
            transition-all duration-1000 ease-out delay-200
            ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
          `}>
          {}
          <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute bottom-[10%] -right-[10%] w-[60%] h-[60%] bg-blue-50 rounded-full blur-3xl opacity-50" />
          </div>

          <div className="relative z-10 max-w-xl mx-auto lg:mx-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-secondary rounded-lg shadow-sm border border-border/50">
                <Briefcase className="w-6 h-6 text-foreground" />
              </div>
              <span className="text-sm font-semibold tracking-wider text-muted-foreground uppercase">
                Professional Services
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              Client Intake <br />
              <span className="text-primary">Perfected.</span>
            </h2>

            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              For Law Firms and Consultancies, every lead matters. 
              Our AI agents conduct professional, detailed client intake interviews, ensuring you only speak to qualified prospects.
            </p>

            <ul className="space-y-4 mb-10">
              {[{
              icon: ClipboardCheck,
              text: "Detailed Case Information Gathering"
            }, {
              icon: ShieldCheck,
              text: "Confidentiality & Conflict Checks"
            }, {
              icon: Clock,
              text: "Direct Calendar Integration"
            }].map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-foreground" />
                  </div>
                  <span className="text-foreground font-medium">{item.text}</span>
                </li>)}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to="/SolutionsPage">
                  Explore Legal AI
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="rounded-full px-8 hover:bg-secondary group">
                <Link to="/ContactUsPage" className="flex items-center gap-2">
                  Contact Sales <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

            {}
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-muted group">
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/professional_services_intake_dashboard.png" alt="AI Legal Intake Interface" className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute bottom-4 left-4 right-4 z-20">
                <div className="bg-card/95 backdrop-blur-sm p-4 rounded-lg border border-border shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                    <p className="text-xs font-medium text-muted-foreground">
                      AI Agent: "I've noted the details of your case. Based on this, I can schedule a consultation for tomorrow at 10 AM."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}